/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */


class MobileProductGridToggle extends HTMLElement {
  connectedCallback() {
    if (this._initialized) return;

    this.productList = this.closest('#facet-main')?.querySelector('.product-list__inner');
    if (!this.productList) return;

    this._initialized = true;
    this._onClick = this._onClick.bind(this);
    this.addEventListener('click', this._onClick);

    let savedColumns;

    try {
      savedColumns = localStorage.getItem('seea-mobile-product-columns');
    } catch (error) {
      savedColumns = null;
    }

    const defaultColumns = this.dataset.defaultColumns === '1' ? '1' : '2';
    const initialColumns = savedColumns === '1' || savedColumns === '2' ? savedColumns : defaultColumns;

    this.setColumns(initialColumns, false);
  }

  disconnectedCallback() {
    if (this._onClick) {
      this.removeEventListener('click', this._onClick);
    }

    this._initialized = false;
  }

  _onClick(event) {
    const button = event.target.closest('[data-mobile-grid-columns]');
    if (!button || !this.contains(button)) return;

    this.setColumns(button.dataset.mobileGridColumns, true);
  }

  setColumns(columns, announceChange) {
    this.productList.dataset.mobileColumns = columns;

    this.querySelectorAll('[data-mobile-grid-columns]').forEach((button) => {
      button.setAttribute('aria-pressed', button.dataset.mobileGridColumns === columns ? 'true' : 'false');
    });

    this.productList.querySelectorAll('img[sizes]').forEach((image) => {
      if (!image.dataset.mobileGridDefaultSizes) {
        image.dataset.mobileGridDefaultSizes = image.getAttribute('sizes');
      }

      const defaultSizes = image.dataset.mobileGridDefaultSizes;

      if (columns === '1') {
        image.setAttribute('sizes', defaultSizes.replace(/^\(max-width:\s*740px\)\s*calc\([^)]*\)/, '(max-width: 740px) calc(100vw - 36px)'));
      } else {
        image.setAttribute('sizes', defaultSizes);
      }
    });

    try {
      localStorage.setItem('seea-mobile-product-columns', columns);
    } catch (error) {
      // The layout still works when storage is unavailable.
    }

    if (announceChange) {
      const status = this.querySelector('[data-mobile-grid-status]');
      status.textContent = columns === '1' ? 'Showing one product per row' : 'Showing two products per row';
    }
  }
}

if (!customElements.get('mobile-product-grid-toggle')) {
  customElements.define('mobile-product-grid-toggle', MobileProductGridToggle);
}

const el = document.querySelector("#menu-item-1 a");
const em = document.querySelector("#desktop-menu-1");

el.addEventListener("click", function(event) {
  event.preventDefault();  
  //toggle attribute
  em.toggleAttribute("hidden");
});

const el2 = document.querySelector("#menu-item-2 a");
const em2 = document.querySelector("#desktop-menu-2");

el2.addEventListener("click", function(event) {
  event.preventDefault();  
  //toggle attribute
  em2.toggleAttribute("hidden");
});

const el3 = document.querySelector("#menu-item-3 a");
const em3 = document.querySelector("#desktop-menu-3");

el3.addEventListener("click", function(event) {
  event.preventDefault();  
  //toggle attribute
  em3.toggleAttribute("hidden");
});

const el4 = document.querySelector("#menu-item-4 a");
const em4 = document.querySelector("#desktop-menu-4");

el4.addEventListener("click", function(event) { 
  event.preventDefault(); 
  //toggle attribute
  em4.toggleAttribute("hidden");
});


function showDiscountMessage(cart) {
  const wrapper = document.querySelector('#cart-discount-wrapper');
  if (!wrapper) return;

  const discountTag = wrapper.dataset.discountTag;
  const discountMessage = wrapper.dataset.discountMessage;

  const fetches = cart.items.map(item =>
    fetch(`/products/${item.handle}.js`).then(res => res.json())
  );

  Promise.all(fetches).then(products => {
    const eligible = products.some(product =>
      product.tags.includes(discountTag)
    );

    const existing = document.querySelector('.cart-discount-message');
    if (existing) existing.remove();

    if (eligible) {
      const messageEl = document.createElement('div');
      messageEl.className = 'cart-discount-message';
      messageEl.innerHTML = discountMessage;
      wrapper.appendChild(messageEl);
    }
  });
}

// Run once on initial page load
fetch('/cart.js')
  .then(res => res.json())
  .then(cart => showDiscountMessage(cart));

// Run every time the cart updates via AJAX
document.addEventListener('cart:updated', function(event) {
  const cart = event.detail.cart;
  showDiscountMessage(cart);
});
